import Icon from '@/components/icons/Icon';
import { classnames } from '@/helpers';
import { NavItem, NavItemIdentifier } from '@/types/navigation';
import { Link } from 'react-router-dom';

const getLinkProps = <T,>(props: T, handleClick = null): T & { onClick?: () => void } => ({
  ...props,
  ...(handleClick && { onClick: handleClick }),
});

const getNavBarItemClasses = (item: NavItem) =>
  classnames(
    'items-center rounded-full inline-flex gap-xs relative',
    item.breakpoint && `hidden ${item.breakpoint}`,
    item.selected ? '!text-black-900' : '!text-black-600',
  );

type NavBarItemProps = {
  item: NavItem;
  className?: string;
  handleClick?: () => void;
  badge?: number;
};

const Badge = ({ badge }: { badge: number }) => {
  return (
    <div className="bg-danger-700 text-xxs absolute -top-[6.5px] left-[16px] block h-[16px] min-w-[16px] rounded-full px-1 text-center text-white">
      {badge > 5 ? '5+' : badge}
    </div>
  );
};

const NavBarItem = ({ item, badge, className, handleClick, ...props }: NavBarItemProps) => {
  const showBadge = !!Number(badge) && badge > 0;
  const isLink = item?.to;
  if (isLink) {
    return item.to.startsWith('/') ? (
      <Link
        id={item.id}
        to={{ pathname: item.to, state: { fromNavigation: true } }}
        className={getNavBarItemClasses(item)}
        {...getLinkProps(props, handleClick)}>
        <Icon
          variant={item.icon}
          size="sm"
          style={{ width: 24, height: 24 }}
          color={item.selected ? 'black-900' : 'black-500'}
        />
        {item.label && (
          <small className={`text-xxs !leading-3 ${item.selected ? 'text-black-900' : 'text-black-600'}`}>
            {item.label}
          </small>
        )}
        {showBadge && <Badge badge={badge} />}
      </Link>
    ) : (
      <a id={item.id} href={item.to} className={getNavBarItemClasses(item)} {...getLinkProps(props, handleClick)}>
        <Icon
          variant={item.icon}
          size="sm"
          color={item.selected ? 'black-900' : 'black-500'}
          style={{ width: 24, height: 24 }}
        />
        {item.label && (
          <small className={`text-xxs !leading-3 ${item.selected ? 'text-black-900' : 'text-black-600'}`}>
            {item.label}
          </small>
        )}
      </a>
    );
  }

  return (
    <button id={item.id} className={getNavBarItemClasses(item) + ' outline-none'} {...getLinkProps(props, handleClick)}>
      <Icon
        variant={item.icon}
        size="sm"
        alt={item.identifier === NavItemIdentifier.Hamburger ? 'meny' : ''}
        color={item.selected ? 'black-900' : 'black-500'}
        style={{ width: 24, height: 24 }}
      />
      {item.label && (
        <small className={`text-xxs !leading-3 ${item.selected ? 'text-black-900' : 'text-black-600'}`}>
          {item.label}
        </small>
      )}
    </button>
  );
};

export default NavBarItem;
